/**
 * /help's two cards: a numbered section, and a grid of terms that each show a formula, a list and a note.
 * An absence ("— …") is drawn dashed and muted, not as a peer of the real formulas.
 *
 * @module components/help/HelpCards
 */
import React from 'react';
import { clsx } from 'clsx';
import type { HelpAnchor } from './metricHelpRegistry';
import type { HelpTerm } from './helpContent';

/** The card a "?" read-more (or a NAV link) arrived at. Whole class names, never built. */
const TARGET_RING = 'ring-2 ring-ink-950 ring-offset-2';

/** Numbered section card — the same visual grammar Insights/Dashboard use. */
export const SectionCard: React.FC<{
  id: HelpAnchor;
  n: number;
  title: string;
  hint?: string;
  targeted?: boolean;
  children: React.ReactNode;
}> = ({ id, n, title, hint, targeted = false, children }) => (
  <section
    id={id}
    tabIndex={-1}
    data-help-target={targeted || undefined}
    className={clsx("scroll-mt-20 bg-white rounded-xl border border-neutral-200 p-5 focus:outline-none", targeted && TARGET_RING)}
  >
    <h2 className="flex items-center gap-2.5 text-[15px] font-extrabold text-ink-900">
      <span className="inline-flex h-6 w-6 items-center justify-center rounded-lg border-[1.5px] border-ink-950 bg-primary-500 text-xs font-extrabold">
        {n}
      </span>
      {title}
    </h2>
    {hint && <p className="mt-0.5 mb-3 text-xs text-neutral-700 pl-[34px]">{hint}</p>}
    <div className={hint ? "" : "mt-3"}>{children}</div>
  </section>
);

const TermCard: React.FC<{ term: HelpTerm; targeted: boolean }> = ({ term: d, targeted }) => {
  const absent = d.formula?.startsWith("—") ?? false;
  return (
    <div
      id={d.id}
      tabIndex={d.id ? -1 : undefined}
      data-help-target={targeted || undefined}
      className={clsx(
        "scroll-mt-20 rounded-lg border p-3 focus:outline-none",
        absent ? "border-dashed border-neutral-300 bg-neutral-50" : "border-neutral-200",
        targeted && TARGET_RING,
      )}
    >
      <p className={`text-sm font-extrabold ${absent ? "text-neutral-700" : "text-ink-900"}`}>{d.term}</p>
      {d.formula && (
        <code className="mt-1 inline-block rounded-md border border-neutral-200 bg-neutral-100 px-2 py-0.5 font-mono text-xs text-ink-900">
          {d.formula}
        </code>
      )}
      {d.lines && (
        <ul className="mt-1.5 space-y-1 text-xs text-ink-900">
          {d.lines.map((line) => <li key={line}>• {line}</li>)}
        </ul>
      )}
      {d.note && <p className="mt-1.5 text-xs text-neutral-700">{d.note}</p>}
    </div>
  );
};

/** `target` is the anchor a "?" arrived at; only the card carrying it is marked. */
export const TermGrid: React.FC<{ terms: readonly HelpTerm[]; target: HelpAnchor | null }> = ({ terms, target }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
    {terms.map((d) => <TermCard key={d.id ?? d.term} term={d} targeted={d.id !== undefined && d.id === target} />)}
  </div>
);
